import React from 'react';
import { RouteProps } from 'react-router';
import { EnvironmentService } from '../environment/environment-service';
import { lazyWithRetry } from '../utilFunctions/react-utils';
import { EffectiveRoleViewAuthorizationService } from '../v2/api/authorization/service';
import { FeatureAccessService } from '../v2/api/featureaccess/service';
import { FeatureFlagsService } from '../v2/api/featureflags/service';
import CustomDashboardWrapper from '../v2/common/components/dashboards/CustomDashboardWrapper';
import FallbackComponent from '../v2/common/components/errors/pages/Fallback';
import { ROUTE_URLS } from '../v2/constants';

export interface CommonRouteProps extends RouteProps {
  id: string;
  isEnabled?: () => boolean;
  comp: () => React.ComponentType<any>;
}

export type PublicRouteProps = CommonRouteProps;
export type AdminRouteProps = CommonRouteProps;
export type PanalytSuperAdminRouteProps = CommonRouteProps;
export type DashboardRouteProps = CommonRouteProps;

const commonPublicRoutes: CommonRouteProps[] = [
  {
    id: 'somethingWentWrong',
    path: ROUTE_URLS.SOMETHING_WENT_WRONG.getFullPath(),
    comp: () => FallbackComponent,
  },
];

const domainsWithSamlLogin = [
  'valentinsaml.panalyt.com',
  'jt.panalyt.com',
  'jt2.panalyt.com',
];

export const getLoginPage = (environmentService: EnvironmentService) => {
  if (
    domainsWithSamlLogin.some((domain) =>
      window.location.origin.includes(domain)
    )
  ) {
    return <SamlLoginPage />;
  } else if (environmentService.isCrexta) {
    return <CrextaLoginPage />;
  } else {
    return <LoginPage />;
  }
};

export const getLoginRoute = (environmentService: EnvironmentService) => {
  if (
    domainsWithSamlLogin.some((domain) =>
      window.location.origin.includes(domain)
    )
  ) {
    return SamlLoginPage;
  } else if (environmentService.isCrexta) {
    return CrextaLoginPage;
  } else {
    return LoginPage;
  }
};

export const publicRoutes: PublicRouteProps[] = [
  {
    id: 'forgotpassword',
    path: ROUTE_URLS.FORGOT_PASSWORD.getFullPath(),
    comp: () => ForgotPasswordPageComponent,
  },
  {
    id: 'reset',
    path: ROUTE_URLS.RESET_PASSWORD.getFullPath(),
    comp: () => ResetPasswordPage,
  },
  {
    id: 'samlError',
    path: ROUTE_URLS.LOGIN_ERROR.getFullPath(),
    comp: () => LoginErrorPage,
  },
  {
    id: 'setup-error',
    path: ROUTE_URLS.SETUP_ERROR.getFullPath(),
    comp: () => ErrorFallbackCrexta,
  },
  ...commonPublicRoutes,
];

export const dataUploadRoute = (
  authorizationService: EffectiveRoleViewAuthorizationService
) => ({
  id: 'adminDataUpload',
  path: ROUTE_URLS.DATA_UPLOAD.getFullPath(),
  comp: () => DataUpload,
  isEnabled: () => authorizationService.canUploadClientRawData(),
});

export const myPanalytRoute = {
  id: 'myPanalyt',
  path: ROUTE_URLS.MY_PANALYT.getFullPath(),
  comp: () => MyPanalyt,
};

// Lazy loaded public route components
const LazyErrorFallbackCrexta = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ '../../ErrorFallbackCrexta')
);
const LazyCrextaLoginPage = lazyWithRetry(
  () =>
    import(/* webpackPrefetch: true */ '../../pages/login/crexta-login-page')
);
const LazyForgotPasswordPageComponent = lazyWithRetry(
  () =>
    import(/* webpackPrefetch: true */ '../../pages/login/forgot-password-page')
);
const LazyLoginPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ '../../pages/login/login-page')
);
const LazyLoginErrorPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ '../../pages/login/LoginErrorPage')
);
const LazyResetPasswordPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ '../../pages/login/reset-password')
);
const LazySamlLoginPage = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ '../../pages/login/saml-login-page')
);

// Lazy loaded MyPanalyt component
const LazyMyPanalytPage = lazyWithRetry(
  () =>
    import(/* webpackPrefetch: true */ '../../pages/my-panalyt/my-panalyt-page')
);

// Lazy loaded admin components
const LazyAlias = lazyWithRetry(
  () => import(/* webpackPrefetch: true */ '../../pages/admin/alias/Alias')
);
const LazyDataUpload = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/admin/data-upload/DataUpload'
    )
);
const LazyUserPermissions = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/admin/user/permissions/UserPermissions'
    )
);
const LazyEditRolePermissions = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/admin/user/permissions/EditRolePermissions'
    )
);

// Lazy loaded superadmin components
const LazyCompanyList = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/CompanyList'
    )
);
const LazyAuditLogs = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/audit-logs/AuditLogs'
    )
);
const LazyManageDomainSettings = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/ManageDomainSettings'
    )
);
const LazyManageDomainPreferences = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/ManageDomainPreferences'
    )
);
const LazyDomainPreferences = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/domain-preferences/DomainPreferences'
    )
);
const LazyCompanyDashboards = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/CompanyDashboards'
    )
);
const LazyManageMetrics = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/ManageMetrics'
    )
);
const LazyMultiDomainAuth = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/MultiDomainAuth'
    )
);
const LazyBackendApi = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/BackendApi'
    )
);
const LazyBatchActions = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/company/BatchActions'
    )
);
const LazyChappiConversationsOverview = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/panalyt-superadmin/chappi-conversations/ChappiConversationsOverview'
    )
);

// Lazy loaded dashboard components
const LazyPeopleSnapshot = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/people-snapshot/PeopleSnapshot'
    )
);
const LazyDiversity = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/diversity/Diversity'
    )
);
const LazyAttrition = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/attrition/Attrition'
    )
);
const LazyJoinersAndLeavers = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/joiners-leavers/JoinersAndLeavers'
    )
);
const LazyMedianBase = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/median-base/MedianBase'
    )
);
const LazyRecruitment = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/recruitment/Recruitment'
    )
);
const LazySurveyDashboard = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/survey-dashboard/SurveyDashboard'
    )
);
const LazyEvaluation = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/evaluation/Evaluation'
    )
);
const LazyMovement = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/movements/Movements'
    )
);
const LazyPayroll = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/payroll-dashboard/Payroll'
    )
);
const LazyTimeAndAttendance = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/time-and-attendance/TimeAndAttendance'
    )
);
const LazyPeopleBalanceSheet = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/balance-sheet/dashboards/people-balance-sheet/PeopleBalanceSheetDashboard'
    )
);
const LazyRecruitmentBalanceSheet = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../../pages/dashboard/balance-sheet/dashboards/recruitment-balance-sheet/RecruitmentBalanceSheetDashboard'
    )
);
const LazyHCSimulationDashboard = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true */ '../v2/pages/dashboards/HCSimulationDashboard/HCSimulationDashboard'
    )
);

// Wrapper components for lazy loading - Public Routes
const ErrorFallbackCrexta = () => <LazyErrorFallbackCrexta />;

const CrextaLoginPage = () => <LazyCrextaLoginPage />;

const ForgotPasswordPageComponent = () => <LazyForgotPasswordPageComponent />;

const LoginPage = () => <LazyLoginPage />;

const LoginErrorPage = () => <LazyLoginErrorPage />;

const ResetPasswordPage = () => <LazyResetPasswordPage />;

const SamlLoginPage = () => <LazySamlLoginPage />;

// Wrapper component for MyPanalyt
const MyPanalyt = () => {
  return <LazyMyPanalytPage />;
};

// Wrapper components for lazy loading - Admin
const Alias = () => <LazyAlias />;

const DataUpload = () => <LazyDataUpload />;

const UserPermissions = () => <LazyUserPermissions />;

const EditRolePermissionsReadWrite = () => (
  <LazyEditRolePermissions isReadonly={false} />
);

const EditRolePermissionsReadOnly = () => (
  <LazyEditRolePermissions isReadonly={true} />
);

// Wrapper components for lazy loading - Superadmin
const CompanyList = () => <LazyCompanyList />;

const AuditLogs = () => <LazyAuditLogs />;

const ManageDomainSettings = () => <LazyManageDomainSettings />;

const ManageDomainPreferences = () => <LazyManageDomainPreferences />;

const DomainPreferences = () => <LazyDomainPreferences />;

const CompanyDashboards = () => <LazyCompanyDashboards />;

const ManageMetrics = () => <LazyManageMetrics />;

const MultiDomainAuth = () => <LazyMultiDomainAuth />;

const BackendApi = () => <LazyBackendApi />;

const BatchActions = () => <LazyBatchActions />;

const ChappiConversationsOverview = () => <LazyChappiConversationsOverview />;

const PeopleSnapshot = () => <LazyPeopleSnapshot />;

const Diversity = () => <LazyDiversity />;

const Attrition = () => <LazyAttrition />;

const JoinersAndLeavers = () => <LazyJoinersAndLeavers />;

const MedianBase = () => <LazyMedianBase />;

const Recruitment = () => <LazyRecruitment />;

const SurveyDashboard = () => <LazySurveyDashboard />;

const Evaluation = () => <LazyEvaluation />;

const Movement = () => <LazyMovement />;

const Payroll = () => <LazyPayroll />;

const TimeAndAttendance = () => <LazyTimeAndAttendance />;

const PeopleBalanceSheet = () => <LazyPeopleBalanceSheet />;

const RecruitmentBalanceSheet = () => <LazyRecruitmentBalanceSheet />;

const HCSimulationDashboard = () => <LazyHCSimulationDashboard />;

export const adminRoutes: (
  featureAccessService: FeatureAccessService,
  featureFlagsService: FeatureFlagsService,
  authorizationService: EffectiveRoleViewAuthorizationService
) => AdminRouteProps[] = (
  featureAccessService: FeatureAccessService,
  featureFlagsService: FeatureFlagsService,
  authorizationService: EffectiveRoleViewAuthorizationService
) => [
  {
    id: 'adminAlias',
    path: ROUTE_URLS.ALIAS.getFullPath(),
    comp: () => Alias,
    isEnabled: () => featureFlagsService.isAliasEnabled(),
  },
  {
    id: 'userPermissions',
    path: ROUTE_URLS.PERMISSIONS.getFullPath(),
    comp: () => UserPermissions,
    isEnabled: () => featureAccessService.canViewUserPermissions(),
  },
  {
    id: 'editRolePermissions',
    path: ROUTE_URLS.EDIT_ROLE_PERMISSIONS.getFullPath(),
    comp: () => EditRolePermissionsReadWrite,
    isEnabled: () => featureAccessService.canWriteRoleSettings(),
  },
  {
    id: 'viewRolePermissions',
    path: ROUTE_URLS.VIEW_ROLE_PERMISSIONS.getFullPath(),
    // just adding another url to the same page for readonly view of permissions
    comp: () => EditRolePermissionsReadOnly,
    isEnabled: () => featureAccessService.canViewRoleSettings(),
  },
  dataUploadRoute(authorizationService),
];

export const panalytSuperAdminRoutes: () => PanalytSuperAdminRouteProps[] =
  () => [
    {
      id: 'panalytSuperadminCompany',
      path: ROUTE_URLS.COMPANY_LIST.getFullPath(),
      comp: () => CompanyList,
    },
    {
      id: 'panalytSuperadminAuditLogs',
      path: ROUTE_URLS.AUDIT_LOGS.getFullPath(),
      comp: () => AuditLogs,
    },
    {
      id: 'panalytSuperadminManageDomainSettings',
      path: `${ROUTE_URLS.MANAGE_DOMAIN_SETTINGS.getFullPath()}/:domain?`,
      comp: () => ManageDomainSettings,
    },
    {
      id: 'panalytSuperadminManageDomainPreferences',
      path: `${ROUTE_URLS.MANAGE_DOMAIN_PREFERENCES.getFullPath()}/:domain?`,
      comp: () => ManageDomainPreferences,
    },
    {
      id: 'domainPreferencesAdminPage',
      path: ROUTE_URLS.DOMAIN_PREFERENCES.getFullPath(),
      comp: () => DomainPreferences,
    },
    {
      id: 'panalytSuperadminManageCompanyDashboards',
      path: `${ROUTE_URLS.COMPANY_DASHBOARDS.getFullPath()}/:domain?`,
      comp: () => CompanyDashboards,
    },
    {
      id: 'panalytSuperadminManageMetrics',
      path: `${ROUTE_URLS.MANAGE_METRICS.getFullPath()}/:domain?`,
      comp: () => ManageMetrics,
    },
    {
      id: 'panalytSuperadminMultiDomainAuth',
      path: `${ROUTE_URLS.MULTI_DOMAIN_AUTH.getFullPath()}/:domain?`,
      comp: () => MultiDomainAuth,
    },
    {
      id: 'panalytSuperadminBackendApi',
      path: `${ROUTE_URLS.BACKEND_API.getFullPath()}/:domain?`,
      comp: () => BackendApi,
    },
    {
      id: 'panalytSuperadminBatchActions',
      path: `${ROUTE_URLS.BATCH_ACTIONS.getFullPath()}/:domain?`,
      comp: () => BatchActions,
    },
    {
      id: 'panalytSuperadminChappiConversations',
      path: `${ROUTE_URLS.CHAPPI_CONVERSATIONS.getFullPath()}/:domain?`,
      comp: () => ChappiConversationsOverview,
    },
  ];

export const dashboardRoutes: DashboardRouteProps[] = [
  {
    id: 'peopleSnapshot',
    path: `${ROUTE_URLS.PEOPLE_SNAPSHOT.getFullPath()}/:pageNumber?`,
    comp: () => PeopleSnapshot,
  },
  {
    id: 'genderDiversity',
    path: `${ROUTE_URLS.DIVERSITY_GENDER.getFullPath()}/:pageNumber?`,
    comp: () => Diversity,
  },
  {
    id: 'attrition',
    path: `${ROUTE_URLS.ATTRITION.getFullPath()}/:pageNumber?`,
    comp: () => Attrition,
  },
  {
    id: 'joinersAndLeavers',
    path: `${ROUTE_URLS.JOINERS_AND_LEAVERS.getFullPath()}/:pageNumber?`,
    comp: () => JoinersAndLeavers,
  },
  {
    id: 'medianBase',
    path: `${ROUTE_URLS.MEDIAN_BASE.getFullPath()}/:pageNumber?`,
    comp: () => MedianBase,
  },
  {
    id: 'recruitment',
    path: `${ROUTE_URLS.RECRUITMENT.getFullPath()}/:pageNumber?`,
    comp: () => Recruitment,
  },
  {
    id: 'survey',
    path: `${ROUTE_URLS.SURVEY.getFullPath()}/:pageNumber?`,
    comp: () => SurveyDashboard,
  },
  {
    id: 'evaluation',
    path: `${ROUTE_URLS.EVALUATION.getFullPath()}/:pageNumber?`,
    comp: () => Evaluation,
  },
  {
    id: 'movement',
    path: `${ROUTE_URLS.MOVEMENT.getFullPath()}/:pageNumber?`,
    comp: () => Movement,
  },
  {
    id: 'payroll',
    path: `${ROUTE_URLS.PAYROLL.getFullPath()}/:pageNumber?`,
    comp: () => Payroll,
  },
  {
    id: 'workHoursAndOt',
    path: `${ROUTE_URLS.WORK_HOURS_AND_OT.getFullPath()}/:pageNumber?`,
    comp: () => TimeAndAttendance,
  },
  {
    id: 'peopleBalanceSheet',
    path: `${ROUTE_URLS.PEOPLE_BALANCE_SHEET.getFullPath()}/:pageNumber?`,
    comp: () => PeopleBalanceSheet,
  },
  {
    id: 'recruitmentBalanceSheet',
    path: `${ROUTE_URLS.RECRUITMENT_BALANCE_SHEET.getFullPath()}/:pageNumber?`,
    comp: () => RecruitmentBalanceSheet,
  },
  {
    id: 'headcount-simulation',
    path: `${ROUTE_URLS.HEADCOUNT_SIMULATION.getFullPath()}/:pageNumber?`,
    comp: () => HCSimulationDashboard,
  },
];

export const customDashboardRoute = {
  id: 'customDashboard',
  path: '/dashboard/custom/:dashboardId/:pageNumber?',
  comp: () => CustomDashboardWrapper,
};
